<script setup lang="ts">
import AvatarCropperModal from '@/components/avatar/AvatarCropperModal.vue';
import { useToast } from '@/composables/useToast';
import { useI18n } from '@/i18n';
import { uploadAvatarImage } from '@/lib/uploadImage';
import router from '@/router';
import { createProfileSchema, type ProfileFormValues } from '@/schemas/profile';
import { useUserStore } from '@/stores/user';
import { Avatar, Button, Divider, Form, Heading, Input, LayoutGap, Modal, Prose } from '@dabble/quillui';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm, useIsFormDirty } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';

const { t, te } = useI18n();
const userStore = useUserStore();
const { showToast } = useToast();

function translateFieldError(raw: string | undefined) {
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
}

const isDeleteModalOpen = ref(false);
const isLoading = ref(false);
const isCropperOpen = ref(false);
const isApplyingAvatar = ref(false);

const rawProfileSchema = computed(() => createProfileSchema((key: string) => t(key)));
const validationSchema = computed(() => toTypedSchema(rawProfileSchema.value));

const { handleSubmit, defineField, values, resetForm, validateField } = useForm<ProfileFormValues>({
  validationSchema,
  initialValues: {
    name: '',
    email: '',
  },
});

const isValid = computed(() =>
  rawProfileSchema.value.isValidSync({
    name: values.name ?? '',
    email: values.email ?? '',
  }),
);

const isDirty = useIsFormDirty();

const fieldOpts = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnModelUpdate: true,
  props: (state: { errors: string[] }) => ({
    error: state.errors.length > 0,
    errorMessage: translateFieldError(state.errors[0]),
  }),
};

const [name, nameField] = defineField('name', fieldOpts);
const [email, emailField] = defineField('email', fieldOpts);

onMounted(async () => {
  if (userStore.user.uid) {
    await userStore.fetchUser(userStore.user.uid);
  }
  const user = userStore.user;
  resetForm({
    values: {
      name: user.name || '',
      email: user.email || '',
    },
  });
});

function openDeleteModal() {
  isDeleteModalOpen.value = true;
}

function closeDeleteModal() {
  isDeleteModalOpen.value = false;
}

async function saveProfile(nextName: string, nextEmail: string) {
  isLoading.value = true;
  try {
    await userStore.updateAccount({ name: nextName, email: nextEmail });
    resetForm({ values: { name: nextName, email: nextEmail } });
    showToast('success', t('account_updated'));
  } catch (error: any) {
    // updateAccount throws before mutating the store, so userStore.user.{name,email}
    // still hold the pre-attempt values — roll the inputs back so the user sees
    // their change has not been saved instead of a silently-stale input.
    resetForm({
      values: {
        name: userStore.user.name,
        email: userStore.user.email,
      },
    });
    showToast('danger', error.message);
  } finally {
    isLoading.value = false;
  }
}

const onSubmit = handleSubmit(async (formValues) => {
  await saveProfile(formValues.name || '', formValues.email || '');
});

async function autoSaveField(field: 'name' | 'email') {
  if (isLoading.value) return;
  const result = await validateField(field);
  if (!result.valid) return;

  const nextName = name.value || '';
  const nextEmail = email.value || '';
  const nameUnchanged = nextName === (userStore.user.name || '');
  const emailUnchanged = nextEmail === (userStore.user.email || '');
  if (nameUnchanged && emailUnchanged) return;

  await saveProfile(nextName, nextEmail);
}

async function onAvatarApply(blob: Blob) {
  const uid = userStore.user.uid;
  if (!uid) return;
  isApplyingAvatar.value = true;
  try {
    const url = await uploadAvatarImage(blob, uid);
    await userStore.updateAccount({
      name: userStore.user.name || '',
      email: userStore.user.email || '',
      photoUrl: url,
    });
    showToast('success', t('account_avatar_updated'));
    isCropperOpen.value = false;
  } catch (error: any) {
    showToast('danger', t('account_avatar_upload_failed'));
  } finally {
    isApplyingAvatar.value = false;
  }
}

async function onAvatarRemove() {
  try {
    await userStore.updateAccount({
      name: userStore.user.name || '',
      email: userStore.user.email || '',
      photoUrl: '',
    });
    showToast('success', t('account_avatar_removed'));
  } catch (error: any) {
    showToast('danger', error.message);
  }
}

async function handleDeleteUser() {
  try {
    await userStore.deleteUser(userStore.user);
    closeDeleteModal();
    showToast('success', t('account_deleted'));
    router.push('/auth/signup');
  } catch (error: any) {
    showToast('danger', error.message);
  }
}
</script>

<template>
  <LayoutGap direction="column" align="stretch" size="medium" fill class="profile-view">
    <LayoutGap direction="column" align="stretch" size="xsmall" fill>
      <Heading :level="1">{{ t('account_profile_title') }}</Heading>
      <Divider variant="secondary" size="small" />
    </LayoutGap>

    <LayoutGap direction="column" align="stretch" size="small" fill class="profile-view__content">
      <LayoutGap direction="row" align="left" vertical-align="center" size="small" class="profile-view__avatar">
        <Avatar
          :key="userStore.user.photoUrl || 'no-photo'"
          :url="userStore.user.photoUrl || undefined"
          :name="userStore.user.name"
          :email="userStore.user.email"
          size="xxhuge"
        />
        <LayoutGap direction="column" align="stretch" size="xsmall">
          <Button
            type="button"
            variant="secondary"
            finish="outlined"
            :disabled="isApplyingAvatar"
            @click="isCropperOpen = true"
          >
            {{ t('account_avatar_change') }}
          </Button>
          <Button
            v-if="userStore.user.photoUrl"
            type="button"
            variant="secondary"
            finish="outlined"
            :disabled="isApplyingAvatar"
            @click="onAvatarRemove"
          >
            {{ t('account_avatar_remove') }}
          </Button>
        </LayoutGap>
      </LayoutGap>
      <Form v-model:dirty="isDirty" :valid="isValid" :actionless="true" class="profile-view__form" @submit="onSubmit">
        <LayoutGap direction="column" align="stretch" size="small" fill>
          <Input
            id="profile-name"
            v-model="name"
            v-bind="nameField"
            :placeholder="t('account_placeholder_name')"
            :label="t('name')"
            type="text"
            required
            @focusout="autoSaveField('name')"
          />
          <Input
            id="profile-email"
            v-model="email"
            v-bind="emailField"
            :label="t('email')"
            name="email"
            type="text"
            required
            @focusout="autoSaveField('email')"
          />
          <Button fill-width class="profile-view__submit" type="submit" :disabled="isLoading || !isValid || !isDirty">
            {{ isLoading ? t('account_update_updating') : t('account_update_submit') }}
          </Button>
        </LayoutGap>
      </Form>
      <Button fill-width class="profile-view__delete" variant="danger" finish="outlined" @click="openDeleteModal">
        {{ t('account_delete_open') }}
      </Button>
    </LayoutGap>
  </LayoutGap>
  <AvatarCropperModal v-model:open="isCropperOpen" :applying="isApplyingAvatar" @apply="onAvatarApply" />
  <Modal v-model="isDeleteModalOpen" :title="t('account_delete_modal_title')" @close="closeDeleteModal">
    <LayoutGap direction="column" align="stretch" size="medium" fill>
      <Prose>{{ t('account_delete_confirm') }}</Prose>
      <LayoutGap
        direction="row"
        align="space-between"
        vertical-align="center"
        size="small"
        fill
        class="profile-view__modal-actions"
      >
        <Button type="button" variant="danger" @click="handleDeleteUser">
          {{ t('account_delete_submit') }}
        </Button>
        <Button type="button" @click="closeDeleteModal">
          {{ t('account_cancel') }}
        </Button>
      </LayoutGap>
    </LayoutGap>
  </Modal>
</template>

<style lang="scss" scoped>
.profile-view {
  max-width: 465px;

  &__content {
    background: theme('color-bg-app-panel');
  }

  &__form {
    width: 100%;
  }

  &__submit,
  &__delete {
    margin-top: space('small');
    max-width: 211px;
  }

  &__modal-actions {
    :deep(.button) {
      justify-content: center;
    }
  }
}
</style>
